import type { ToolCallMessagePart } from '@assistant-ui/react';
import { z } from 'zod';

import { ERROR_RECOVERY_STRATEGIES } from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * Successful tool execution result.
 *
 * Dataset-producing tools populate the TanStack Query cache under the
 * canonical `(threadId, toolName, args)` key and include an `outputDataset`
 * name so downstream tools and UI consumers can resolve the dataset
 * through the {@link DatasetResolver}. The dataset itself is never
 * serialised into the tool result — message history persists only the
 * args, and the cache is re-derived from them on demand.
 *
 * Tools that do not produce a Dataset (e.g. task lifecycle tools) simply
 * omit `outputDataset`.
 */
const ToolOutputSuccessSchema = z.object({
  success: z.literal(true),
  /** Human-readable summary of the result */
  message: z.string(),
  /** Key for downstream tools to reference this output */
  outputDataset: z.string().optional(),
});
/** @public */
export type ToolOutputSuccess = z.infer<typeof ToolOutputSuccessSchema>;

/**
 * Shared success schema for tools that produce a {@link Dataset} via the
 * data-pipeline step registry.
 *
 * Acts as the SSOT for the producer-tool result contract so each producer
 * tool inherits the same shape and tool-specific extras extend from a
 * single base. The `outputDataset` name is the LLM-facing handle that
 * later tools reference via `inputDataset`, which the {@link DatasetResolver}
 * translates into the canonical `(toolName, args)` TanStack Query key.
 *
 * Tools that produce a dataset should use this schema directly or extend
 * it with tool-specific extras (e.g. precomputed stats, tool-layer
 * summaries).
 */
export const DatasetProducerSuccessSchema = ToolOutputSuccessSchema.extend({
  /** Output dataset name; the canonical dataset query key is derived from `(toolName, args)`. */
  outputDataset: z.string(),
});
/** @public */
export type DatasetProducerSuccess = z.infer<typeof DatasetProducerSuccessSchema>;

/**
 * Failed tool execution result.
 *
 * Mirrors the wire shape produced by
 * {@link ToolExecutionError.toStructuredResponse} on the new error
 * hierarchy. The discriminator on the wire is `errorType` (the class
 * name, e.g. `"ColumnNotFoundError"`) — not a separate string-enum.
 *
 * Structured `details` carries class-specific data the LLM and UI can
 * branch on without parsing the message (e.g. the missing column list,
 * available column list, conflicting filter description).
 */
export const ToolOutputErrorSchema = z.object({
  success: z.literal(false),
  /** Class name of the thrown error — stable, machine-readable discriminator. */
  errorType: z.string(),
  /** Human-readable error message. */
  error: z.string(),
  /** Recovery suggestions for the LLM. */
  suggestions: z.string().array(),
  /** Class-specific structured fields the UI / LLM can branch on. */
  details: z.record(z.string(), z.unknown()),
  /** Recovery strategy hint — what should the LLM do next. */
  recovery: z.enum(ERROR_RECOVERY_STRATEGIES),
});
/** @public */
export type ToolOutputError = z.infer<typeof ToolOutputErrorSchema>;

/**
 * Shared discriminated union for dataset-producing tools.
 *
 * Combines {@link DatasetProducerSuccessSchema} and {@link ToolOutputErrorSchema}
 * into the canonical result contract returned by every tool that stages a
 * {@link Dataset} into the chat-history store. Tools that need tool-specific
 * extras on the success variant should compose their own union from
 * {@link DatasetProducerSuccessSchema} + extras + {@link ToolOutputErrorSchema}
 * rather than duplicating this plain union.
 */
export const DatasetProducerResultSchema = z.union([DatasetProducerSuccessSchema, ToolOutputErrorSchema]);

/** @public */
export type DatasetProducerResult = z.infer<typeof DatasetProducerResultSchema>;

/**
 * Tool Output Contract - discriminated union on `success` field.
 *
 * All tools should return this format for consistent downstream consumption.
 * The discriminated union enables type-safe handling of success/error cases.
 *
 * @example
 * ```ts
 * // Success case — dataset producer (see DatasetProducerSuccessSchema)
 * return {
 *   success: true,
 *   message: `Query returned ${dataset.rows.length} records`,
 *   outputDataset: 'my_query_result',
 *   dataset,
 * };
 *
 * // Success case — non-producer (task lifecycle, etc.)
 * return {
 *   success: true,
 *   message: 'Task completed',
 * };
 *
 * // Error case (produced by ToolExecutionError.toStructuredResponse)
 * return {
 *   success: false,
 *   errorType: 'DatasetNotFoundError',
 *   error: 'Dataset "wells_2024" not found.',
 *   suggestions: ['Available datasets: events_jan, prod_history.', 'Dataset names are case-sensitive.'],
 *   details: { requested: 'wells_2024', available: ['events_jan', 'prod_history'] },
 *   recovery: 'retry-with-different-input',
 * };
 * ```
 */
export const ToolOutputContractSchema = z.discriminatedUnion('success', [
  ToolOutputSuccessSchema,
  ToolOutputErrorSchema,
]);
/** @public */
export type ToolOutputContract = z.infer<typeof ToolOutputContractSchema>;

/**
 * Tool call part extracted from assistant-ui messages.
 *
 * Structural alias of assistant-ui's {@link ToolCallMessagePart} with two
 * relaxations preserved from the historical local definition:
 *
 * - `args` is widened to `Record<string, unknown>` (canonical default is
 *   `ReadonlyJSONObject`). All accesses in this codebase are reads, and
 *   tests construct partial fixtures, so the wider type is harmless.
 * - `argsText` is optional. The canonical type makes it required, but
 *   internal/test constructions don't always materialize it.
 */
export type ToolCallPart = Omit<ToolCallMessagePart<Record<string, unknown>, unknown>, 'argsText'> & {
  argsText?: string;
};

/**
 * Runtime shape of an assistant-ui message: a `role` plus a `content` array
 * of parts. Used to filter raw `unknown[]` message streams before extracting
 * tool calls.
 */
const MessageSchema = z.object({
  role: z.string(),
  content: z.array(z.unknown()),
});

/**
 * Runtime shape of a tool-call message part. Lenient by design — we don't
 * validate `result`, `argsText`, or status fields, which vary across tools
 * and across the streaming lifecycle.
 */
const ToolCallPartSchema = z.object({
  type: z.literal('tool-call'),
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
});

/**
 * Type guard for message objects with role and content array.
 * Used to filter assistant-ui messages before extracting tool calls.
 */
export function isMessage(value: unknown): value is { role: string; content: unknown[] } {
  return MessageSchema.safeParse(value).success;
}

/**
 * Type guard for tool call parts.
 *
 * Lenient on purpose — `result`, `argsText`, and status fields vary by tool
 * and lifecycle stage, so we only validate the keys every tool call carries.
 */
export function isToolCallPart(value: unknown): value is ToolCallPart {
  return ToolCallPartSchema.safeParse(value).success;
}

/**
 * Predicate: does a tool-execution output value look like a failure?
 *
 * Recognises the canonical failure envelopes emitted by `createTool`:
 *   - {@link ToolOutputErrorSchema}: `{ success: false, error: string, ... }`
 *   - SDK-level parse failures: `{ error: string }` without a `success` field
 *
 * Used by the chat runtime to flip a tool-call part into the `output-error`
 * state without relying on substring matching against the rendered string —
 * tools whose `message` happens to mention the word "error" (e.g. "0 errors
 * found") were previously misreported as failures.
 */
export function isToolErrorOutput(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) {
    return false;
  }
  const obj = output as Record<string, unknown>;
  if (obj.success === false) {
    return true;
  }
  if ('error' in obj && obj.error != null) {
    return true;
  }
  return false;
}
